/**
 * Relative-time formatting for the live overview.
 *
 * Pure and deterministic: every function takes an explicit `now` so it can be
 * unit-tested without touching the wall clock. Output is Italian (the app's
 * primary locale).
 *
 * Freshness buckets are derived from the collector's cadence (a poll every
 * 5 minutes, see COLLECTOR.md). One missed poll still lands inside ~10 min, so:
 *   - `fresh` — age ≤ 10 min  (at most one poll late)
 *   - `stale` — age ≤ 30 min  (a few polls missed — degraded, still shown)
 *   - `down`  — age  > 30 min  (collector or feed likely down)
 */

export type Freshness = "fresh" | "stale" | "down";

export interface RelativeTime {
  /** Human label in Italian, e.g. "adesso", "3 min fa", "2 ore fa". */
  label: string;
  /** Coarse freshness bucket derived from age vs. the 5-minute poll cadence. */
  freshness: Freshness;
  /** Age in whole seconds, clamped at 0 (future timestamps read as 0). */
  ageSeconds: number;
}

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const FRESH_MAX_SECONDS = 10 * MINUTE;
const STALE_MAX_SECONDS = 30 * MINUTE;

function freshnessFor(ageSeconds: number): Freshness {
  if (ageSeconds <= FRESH_MAX_SECONDS) return "fresh";
  if (ageSeconds <= STALE_MAX_SECONDS) return "stale";
  return "down";
}

/**
 * Italian "… ago" label for an age in seconds (e.g. "adesso", "3 min fa",
 * "2 ore fa"). Shared by {@link relativeTime} and the collector-status surface,
 * which already has a server-computed age and so skips the ISO parse.
 */
export function formatAgo(ageSeconds: number): string {
  if (ageSeconds < 45) return "adesso";
  if (ageSeconds < 90) return "1 min fa";
  if (ageSeconds < HOUR) return `${Math.round(ageSeconds / MINUTE)} min fa`;
  if (ageSeconds < 90 * MINUTE) return "1 ora fa";
  if (ageSeconds < DAY) return `${Math.round(ageSeconds / HOUR)} ore fa`;
  const days = Math.round(ageSeconds / DAY);
  return days === 1 ? "1 giorno fa" : `${days} giorni fa`;
}

/**
 * Build a {@link RelativeTime} for an ISO-8601 UTC instant relative to `now`.
 *
 * An unparseable timestamp yields a `down` bucket with an em-dash label, so a
 * malformed feed value degrades visibly rather than throwing.
 */
export function relativeTime(isoUtc: string, now: Date): RelativeTime {
  const then = new Date(isoUtc).getTime();
  if (Number.isNaN(then)) {
    return { label: "—", freshness: "down", ageSeconds: Number.POSITIVE_INFINITY };
  }
  const ageSeconds = Math.max(0, Math.round((now.getTime() - then) / 1000));
  return { label: formatAgo(ageSeconds), freshness: freshnessFor(ageSeconds), ageSeconds };
}

const ROME_HOUR_MINUTE = new Intl.DateTimeFormat("it-IT", {
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Rome",
});

const ROME_DAY_MONTH = new Intl.DateTimeFormat("it-IT", {
  day: "2-digit",
  month: "2-digit",
  timeZone: "Europe/Rome",
});

/**
 * Format an instant as a Europe/Rome wall-clock time (e.g. "14:30") — the
 * x-axis tick label for the history chart. Invalid dates render as "—".
 */
export function formatRomeClock(date: Date): string {
  if (Number.isNaN(date.getTime())) return "—";
  return ROME_HOUR_MINUTE.format(date);
}

/** Europe/Rome day/month (e.g. "27/06") — used to date a multi-day window. */
export function formatRomeDayMonth(date: Date): string {
  if (Number.isNaN(date.getTime())) return "—";
  return ROME_DAY_MONTH.format(date);
}

/**
 * Europe/Rome date + wall-clock (e.g. "27/06 14:30") for an absolute timestamp —
 * the collector-status diagnostics. Accepts an ISO string or `null` (→ "—").
 */
export function formatRomeDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return `${ROME_DAY_MONTH.format(date)} ${ROME_HOUR_MINUTE.format(date)}`;
}
